/**
 * The free preview's play counter.
 *
 * A visitor who presses "Start free" on the landing page gets the real app with
 * no account, and can play PREVIEW_LIMIT clips before the wall. The count lives
 * in localStorage, so it is deliberately soft — clearing site data or switching
 * browsers resets it. That is the accepted trade: the alternative is minting an
 * account per visitor, and exports.metrics counts every Auth account as a
 * signup. What is NOT soft is the source URL list, which never reaches the
 * client in bulk (see exports.libraryindex).
 */

export const PREVIEW_LIMIT = 10;

const COUNT_KEY = 'gnosis-preview-plays';
/** Ids already counted, so replaying the same clip doesn't burn a second play. */
const SEEN_KEY = 'gnosis-preview-seen';

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/** How many of the free plays are used up. */
export function previewPlaysUsed(): number {
  try {
    const n = Number(localStorage.getItem(COUNT_KEY));
    return Number.isFinite(n) && n > 0 ? Math.min(n, PREVIEW_LIMIT) : 0;
  } catch {
    // Private browsing can throw on read. Treat it as a fresh visitor rather
    // than walling someone who has watched nothing.
    return 0;
  }
}

export function previewPlaysLeft(): number {
  return Math.max(0, PREVIEW_LIMIT - previewPlaysUsed());
}

export function previewExhausted(): boolean {
  return previewPlaysLeft() === 0;
}

/**
 * Count one clip against the allowance. Returns the plays left afterwards.
 * Idempotent per technique: the feed can re-render a clip without charging for
 * it twice, and someone swiping back through history pays nothing.
 */
export function recordPreviewPlay(techniqueId: string): number {
  try {
    const seen = readSeen();
    if (seen.has(techniqueId)) return previewPlaysLeft();
    seen.add(techniqueId);
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
    localStorage.setItem(COUNT_KEY, String(Math.min(seen.size, PREVIEW_LIMIT)));
  } catch {
    // Storage unavailable — they get an unlimited preview rather than an app
    // that refuses to play anything. Rare, and the friendlier failure.
  }
  return previewPlaysLeft();
}

/**
 * The server says this browser is out, whatever the local count thinks.
 *
 * previewvideo is the authority (see chargePreviewPlay in functions/index.js);
 * this counter is a mirror that puts the wall up without a round trip. When the
 * two disagree — storage cleared, count edited — the server wins and this
 * catches up, so the UI stops offering plays that will be refused.
 */
export function markPreviewExhausted() {
  try {
    localStorage.setItem(COUNT_KEY, String(PREVIEW_LIMIT));
  } catch { /* the server refuses regardless */ }
}

/** Cleared once they have an account, so the counter can't wall a paying user. */
export function clearPreviewPlays() {
  try {
    localStorage.removeItem(COUNT_KEY);
    localStorage.removeItem(SEEN_KEY);
  } catch { /* nothing to clear */ }
}

/** Where the play count waits between clearing it and the first recordVisit. */
const SIGNUP_PLAYS_KEY = 'gnosis-preview-plays-at-signup';

/**
 * Freeze how many free clips it took before they signed up, so recordVisit can
 * report it once. Kept separate from the live counter because that one is wiped
 * at sign-in — otherwise the number the funnel wants is gone before it is sent.
 */
export function stashPreviewPlaysForSignup() {
  try {
    const used = previewPlaysUsed();
    if (used > 0) localStorage.setItem(SIGNUP_PLAYS_KEY, String(used));
  } catch { /* nothing to stash */ }
}

/** The stashed count. Left in place: recordVisit retries on failure, and
 *  reading it away would lose the number on the attempt that didn't land. */
export function previewPlaysAtSignup(): number | undefined {
  try {
    const n = Number(localStorage.getItem(SIGNUP_PLAYS_KEY));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch {
    return undefined;
  }
}

/** Dropped once the server has it. */
export function clearPreviewPlaysAtSignup() {
  try {
    localStorage.removeItem(SIGNUP_PLAYS_KEY);
  } catch { /* nothing to clear */ }
}

/** "Start free": into the app with no account at all. Keeps the query string so
 *  a creator's ?ref= survives — attribution is read from localStorage at
 *  sign-in (src/api/metrics.ts), which may now be several clips later.
 *  Lives here rather than beside the landing page's other CTA handler: that
 *  file exports components, and Fast Refresh wants those files to export only
 *  components. */
export function openFreePreview() {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  window.location.href = base + '/try' + window.location.search;
}
